import { useCallback, useState, useEffect } from "react";
import { generateFileThumbnail } from "@utils/videoEditing";
import { useMediaStore } from "@/store";
import { MediaItem, MediaItemStatus } from "@/types/media";
import { useKeyboardShortcut } from "./useKeyboardShortcut";
import { useMediaUtils } from "./useMediaUtils";

/**
 * Shared hook for file carousel navigation
 * Handles prev/next file navigation with keyboard support
 */
export function useFileCarouselNavigation({
    mediaItems,
    fileId,
    flowLocked,
    hasMultipleFiles
}: {
    mediaItems: MediaItem[];
    fileId?: string;
    flowLocked: boolean;
    hasMultipleFiles: boolean;
}) {
    const { setCurrentMedia } = useMediaUtils();
    const media = useMediaStore();
    const currentIndex = mediaItems.findIndex((f) => f.id === fileId);

    const handlePrevFile = useCallback(() => {
        if (flowLocked || !hasMultipleFiles) return;

        if (mediaItems.length > 0) {
            const prevIndex = currentIndex <= 0 ? mediaItems.length - 1 : currentIndex - 1;

            const targetMediaItem = mediaItems[prevIndex];
            let targetFrame = targetMediaItem;
            if (targetMediaItem.isVideo) {
                const frameId = targetMediaItem.segmentToFrameMediaItemIdMap?.[0];
                targetFrame = media[frameId!];
            }

            setCurrentMedia({ file: targetMediaItem, frame: targetFrame, frameSegment: 0 });
        }
    }, [mediaItems, currentIndex, flowLocked, hasMultipleFiles, setCurrentMedia, media]);

    const handleNextFile = useCallback(() => {
        if (flowLocked || !hasMultipleFiles) return;

        if (mediaItems.length > 0) {
            const nextIndex = currentIndex >= mediaItems.length - 1 ? 0 : currentIndex + 1;

            const targetMediaItem = mediaItems[nextIndex];
            let targetFrame = targetMediaItem;
            if (targetMediaItem.isVideo) {
                const frameId = targetMediaItem.segmentToFrameMediaItemIdMap?.[0];
                targetFrame = media[frameId!];
            }

            setCurrentMedia({ file: targetMediaItem, frame: targetFrame, frameSegment: 0 });
        }
    }, [mediaItems, currentIndex, flowLocked, hasMultipleFiles, setCurrentMedia, media]);

    return { currentIndex, handlePrevFile, handleNextFile };
}

/**
 * Shared hook for generating file carousel thumbnails
 * Generates thumbnails for both images and videos
 */
export function useFileCarouselThumbnails(
    mediaItems: MediaItem[],
    orderedTimelineFramePreviewUrls: any,
    highResolution: boolean = false
) {
    const [thumbnails, setThumbnails] = useState<Record<string, string>>({});
    const [thumbnailResolution, setThumbnailResolution] = useState<Record<string, number>>({});

    useEffect(() => {
        const generateThumbnails = async () => {
            const newThumbnails: Record<string, string> = {};
            const newResolutions: Record<string, number> = {};
            const thumbnailSize = highResolution ? 1024 : 64;

            for (const mediaItem of mediaItems) {
                // Skip thumbnail generation for media items that are still processing (e.g., during re-encoding)
                if (mediaItem.status === MediaItemStatus.PROCESSING) {
                    continue;
                }

                // Regenerate thumbnail if resolution changed or doesn't exist
                const needsRegeneration =
                    !thumbnails[mediaItem.id] ||
                    thumbnailResolution[mediaItem.id] !== thumbnailSize;

                if (needsRegeneration) {
                    if (mediaItem.isVideo) {
                        const cachedThumbnails =
                            orderedTimelineFramePreviewUrls[mediaItem.id]?.[
                                mediaItem.metadata?.frame_count! // MERGE CONFLICT RESOLUTION (verify)
                            ];

                        if (cachedThumbnails && highResolution) {
                            // For high resolution video thumbnails, use cached frame which is already high quality
                            newThumbnails[mediaItem.id] = cachedThumbnails[0];
                            newResolutions[mediaItem.id] = thumbnailSize;
                        } else if (cachedThumbnails && !highResolution) {
                            // For low resolution, use cached thumbnail if available
                            newThumbnails[mediaItem.id] = cachedThumbnails[0];
                            newResolutions[mediaItem.id] = thumbnailSize;
                        } else {
                            // Fallback: generate thumbnail from video file
                            const thumbnail = await generateFileThumbnail(mediaItem.file, {
                                size: thumbnailSize
                            });
                            if (thumbnail) {
                                newThumbnails[mediaItem.id] = thumbnail;
                                newResolutions[mediaItem.id] = thumbnailSize;
                            }
                        }
                    } else {
                        const thumbnail = await generateFileThumbnail(mediaItem.file, {
                            size: thumbnailSize
                        });
                        if (thumbnail) {
                            newThumbnails[mediaItem.id] = thumbnail;
                            newResolutions[mediaItem.id] = thumbnailSize;
                        }
                    }
                }
            }

            if (Object.keys(newThumbnails).length > 0) {
                setThumbnails((prev) => ({ ...prev, ...newThumbnails }));
                setThumbnailResolution((prev) => ({ ...prev, ...newResolutions }));
            }
        };

        generateThumbnails();
    }, [
        mediaItems,
        thumbnails,
        thumbnailResolution,
        orderedTimelineFramePreviewUrls,
        highResolution
    ]);

    return thumbnails;
}

/**
 * Shared hook for keyboard navigation in file carousel
 * Handles up/down and left/right arrow keys for file navigation
 */
export function useFileCarouselKeyboardNavigation(
    hasMultipleFiles: boolean,
    flowLocked: boolean,
    handlePrevFile: () => void,
    handleNextFile: () => void
) {
    const disabled = !hasMultipleFiles || flowLocked;

    // Vertical navigation (up/down)
    useKeyboardShortcut({ key: "ArrowUp", handler: handlePrevFile, disabled });
    useKeyboardShortcut({ key: "ArrowDown", handler: handleNextFile, disabled });

    // Horizontal navigation (left/right) - useful for comparison mode and general UX
    useKeyboardShortcut({ key: "ArrowLeft", handler: handlePrevFile, disabled });
    useKeyboardShortcut({ key: "ArrowRight", handler: handleNextFile, disabled });
}

/**
 * Shared hook for auto-scrolling to current file in carousel
 * Automatically scrolls the thumbnail container to keep the current file centered
 */
export function useFileCarouselAutoScroll({
    fileId,
    hasMultipleFiles,
    containerId
}: {
    fileId?: string;
    hasMultipleFiles: boolean;
    containerId: string;
}) {
    useEffect(() => {
        if (fileId && hasMultipleFiles) {
            const thumbnailElement = document.querySelector(`[data-file-id="${fileId}"]`);
            if (thumbnailElement) {
                const container = document.getElementById(containerId);
                if (container) {
                    const elementRect = thumbnailElement.getBoundingClientRect();
                    const containerRect = container.getBoundingClientRect();

                    // Calculate the scroll position to center the element
                    const elementCenter = elementRect.top + elementRect.height / 2;
                    const containerCenter = containerRect.top + containerRect.height / 2;
                    const scrollOffset = elementCenter - containerCenter;
                    const scrollTop = container.scrollTop + scrollOffset;

                    container.scrollTo({ top: scrollTop, behavior: "smooth" });
                }
            }
        }
    }, [fileId, hasMultipleFiles, containerId]);
}
